/**
 * PatientImportUploadPage Component
 *
 * CSV upload page (step 0) for the patient import wizard.
 * Provides a drag-and-drop zone for CSV file upload with validation feedback.
 */

import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubjectLanguage } from '@features/user';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { capitalize } from '@utils/utils';

import { usePatientImportUpload } from '../hooks';

import { EhrExportGuide } from './EhrExportGuide';
import { PatientImportErrorBoundary } from './PatientImportErrorBoundary';

// =============================================================================
// Helpers
// =============================================================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

// =============================================================================
// Component
// =============================================================================

export const PatientImportUploadPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const subjectLanguage = useSubjectLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    isReady,
    selectedFile,
    isDragging,
    isUploading,
    error,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileInputChange,
    handleUpload,
    clearFile,
    clearError,
  } = usePatientImportUpload();

  return (
    <PatientImportErrorBoundary componentName="PatientImportUploadPage">
    <Box
      sx={{
        maxWidth: 1000,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton
          onClick={() => navigate('/patients')}
          aria-label="Back to patients"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Import {capitalize(subjectLanguage)}s
        </Typography>
      </Box>

      {/* Not ready alert */}
      {!isReady && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Your organization is not set up yet. Please wait for your account to
          be fully configured before importing {subjectLanguage}s.
        </Alert>
      )}

      {/* Upload card */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={1}
          sx={{
            maxWidth: 600,
            width: '100%',
            p: { xs: 3, sm: 4 },
            borderRadius: 2,
          }}
        >
          {/* Title */}
          <Typography variant="h6" sx={{fontWeight: 600,  mb: 3 }}>
            Upload {capitalize(subjectLanguage)} Records
          </Typography>

          {/* Error alert */}
          {error && (
            <Alert severity="error" onClose={clearError} sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Drag-and-drop zone */}
          <Box
            sx={{
              border: `2px dashed ${
                isDragging
                  ? theme.palette.primary.main
                  : theme.palette.divider
              }`,
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: isUploading ? 'default' : 'pointer',
              bgcolor: isDragging
                ? alpha(theme.palette.primary.main, 0.05)
                : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': isUploading
                ? {}
                : {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  },
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => {
              if (!isUploading) {
                fileInputRef.current?.click();
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xml,.xlsx,.zip"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />

            {selectedFile ? (
              <Box>
                <InsertDriveFileIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  {selectedFile.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                >
                  {formatFileSize(selectedFile.size)}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearFile();
                  }}
                  disabled={isUploading}
                  sx={{ mt: 1 }}
                >
                  Remove
                </Button>
              </Box>
            ) : (
              <Box>
                <CloudUploadIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.text.disabled,
                    mb: 1,
                  }}
                />
                <Typography variant="subtitle1" gutterBottom>
                  Drop a file here or click to browse
                </Typography>
              </Box>
            )}
          </Box>

          {/* Info section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              &bull; Existing {subjectLanguage}s will be matched and updated automatically
            </Typography>
            <Typography variant="body2" color="text.secondary">
              &bull; New {subjectLanguage}s will be created
            </Typography>
            <Typography variant="body2" color="text.secondary">
              &bull; You&apos;ll review everything before importing
            </Typography>
          </Box>

          {/* Upload button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleUpload}
            disabled={!selectedFile || !isReady || isUploading}
            startIcon={
              isUploading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CloudUploadIcon />
              )
            }
            sx={{ mt: 3 }}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Paper>
      </Box>

      {/* EHR Export Guide */}
      <EhrExportGuide />
    </Box>
    </PatientImportErrorBoundary>
  );
};
